import React, { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { every, isEmpty } from 'underscore';
import SteppedModal from 'components/SteppedModal';
import useConnectedModal from 'containers/ConnectedModal/useConnectedModal';
import ThreadsPostModal from 'containers/ThreadsPostModal';
import { ThreadsPostModalSteps } from 'containers/ThreadsPostModal/steps/types';
import useAccessDeniedStep from 'containers/ThreadsPostModal/steps/useAccessDeniedStep';
import useAuthenticationProgressStep from 'containers/ThreadsPostModal/steps/useAuthenticationProgressStep';
import useAuthorizationStep from 'containers/ThreadsPostModal/steps/useAuthorizationStep';
import useAutopostingComposerStep from 'containers/ThreadsPostModal/steps/useAutopostingComposerStep';
import { UseComposerStepValue } from 'containers/ThreadsPostModal/steps/useAutopostingComposerStep/types';
import useSwitchAccountStep from 'containers/ThreadsPostModal/steps/useSwitchAccountStep';
import { showError } from 'redux/modules/notification';
import { threadsUserDataSelector } from 'redux/modules/social';
import { ThreadsSheetDefaultValues } from './types';

export interface ThreadsAutoPostModalProps {}

const ACCESS_DENIED_MESSAGE = 'access_denied';
const ACCESS_DENIED_STEP = 'access-denied';
const AUTHORIZATION_STEP = 'authorization';

const ThreadsAutoPostModal: React.FC<ThreadsAutoPostModalProps> = () => {
  const [activeStep, setActiveStep] = useState('');

  const modalRef = useRef<SteppedModal>(null);

  const { params, show, onExited, onHide } = useConnectedModal(
    'ThreadsAutoPost',
  );

  const dispatch = useDispatch();

  const threadsUserData = useSelector(threadsUserDataSelector);

  const { subscriptionItem } = params ?? {};

  const { description } =
    subscriptionItem?.autoPostVideoPreference.options || {};

  const initialValues = React.useMemo((): ThreadsSheetDefaultValues => {
    return {
      description:
        description ??
        'Here’s a new clip from <Episode Title>! Listen to the full episode here: <Link To Episode>',
    };
  }, [description]);

  const replaceModalStep = useCallback((id: ThreadsPostModalSteps): void => {
    modalRef.current?.stepHistory.replace(id);
  }, []);

  const handleGoToProgressStep = useCallback(() => {
    replaceModalStep('authentication-in-progress');
  }, [replaceModalStep]);

  const handleSwitchAccount = useCallback(() => {
    replaceModalStep('switch');
  }, [replaceModalStep]);

  const handleGoToComposerStep = useCallback(() => {
    if (every(threadsUserData, isEmpty)) {
      return undefined;
    }

    return replaceModalStep('composer');
  }, [replaceModalStep, threadsUserData]);

  const handleAuthenticationSuccess = useCallback(async () => {
    replaceModalStep('composer');
  }, [replaceModalStep]);

  const handleHide = useCallback(
    (values?: UseComposerStepValue) => {
      handleGoToComposerStep();
      onHide(values);
    },
    [handleGoToComposerStep, onHide],
  );

  const handleAuthorizationError = useCallback(
    (err: Error) => {
      if (err.message === ACCESS_DENIED_MESSAGE) {
        modalRef?.current?.stepHistory.replace(ACCESS_DENIED_STEP);
      } else {
        modalRef?.current?.stepHistory.replace(AUTHORIZATION_STEP);
        onHide();
        dispatch(showError('Error authenticating with Threads'));
      }
    },
    [dispatch, onHide],
  );

  const handleExited = useCallback(() => {
    handleGoToComposerStep();
    onExited();
  }, [handleGoToComposerStep, onExited]);

  const handleSubmit = useCallback(
    (values: UseComposerStepValue): void => {
      handleHide(values);
    },
    [handleHide],
  );

  const authorization = useAuthorizationStep({
    onAuth: handleGoToProgressStep,
    onAuthSuccess: handleAuthenticationSuccess,
    onError: handleAuthorizationError,
  });

  const authenticationProgress = useAuthenticationProgressStep();

  const accessDenied = useAccessDeniedStep({
    onError: () => null,
    onReviseAccess: () => null,
  });

  const switchAccount = useSwitchAccountStep({
    onSwitchAccount: handleGoToProgressStep,
    onSwitchAccountSuccess: handleAuthenticationSuccess,
    onError: handleAuthorizationError,
  });

  const composer = useAutopostingComposerStep({
    initialValues,
    onSwitchAccountClick: handleSwitchAccount,
    onHideModal: handleHide,
    onSubmit: handleSubmit,
  });

  const isComposerStep = activeStep === 'composer';

  return (
    <ThreadsPostModal
      {...{ show }}
      ref={modalRef}
      title={!isComposerStep && 'auto-post videos'}
      defaultStep={authorization.id}
      steps={[
        authorization,
        authenticationProgress,
        accessDenied,
        switchAccount,
        composer,
      ]}
      className={isComposerStep && 'composer-step-container'}
      onStepChange={setActiveStep}
      onHide={() => handleHide()}
      onExited={handleExited}
    />
  );
};

export default ThreadsAutoPostModal;
